import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const http = axios.create({
  baseURL: API,
  withCredentials: true,
});

/**
 * Silent-refresh interceptor for iOS PWAs (and everyone else).
 *
 * When any request comes back 401, we assume the short-lived access_token
 * expired but the longer-lived refresh_token is still valid — try to mint a
 * new access_token via /auth/refresh, then retry the original request once.
 *
 * Without this, iOS clients get booted to /login every ~24h even though their
 * refresh cookie is fine for 90 days. `_retry` guards prevent infinite loops
 * when refresh itself returns 401 (i.e. genuinely logged out).
 *
 * Routes we deliberately do NOT try to refresh from:
 *   - /auth/login, /auth/refresh, /auth/register → these ARE the auth flow,
 *     a 401 here is a real credential failure and must reach the caller.
 */
const AUTH_ENDPOINTS = ["/auth/login", "/auth/refresh", "/auth/register", "/auth/logout"];
const isAuthEndpoint = (url = "") => AUTH_ENDPOINTS.some((p) => url.includes(p));

// Coalesce concurrent 401 refreshes into a single in-flight promise so a page
// with 4 parallel requests doesn't fire 4 refresh calls.
let refreshInFlight = null;

http.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error.config;
    const status = error?.response?.status;
    if (
      status !== 401 ||
      !original ||
      original._retry ||
      isAuthEndpoint(original.url || "")
    ) {
      return Promise.reject(error);
    }
    original._retry = true;
    try {
      if (!refreshInFlight) {
        refreshInFlight = http.post("/auth/refresh").finally(() => {
          refreshInFlight = null;
        });
      }
      await refreshInFlight;
      return http(original);
    } catch (e) {
      return Promise.reject(error);
    }
  }
);

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    // FastAPI 422 — surface only the first error in a friendly form
    const first = detail[0];
    if (first && typeof first === "object") {
      const field = Array.isArray(first.loc) ? first.loc.filter((p) => p !== "body").join(".") : "";
      const msg = first.msg || JSON.stringify(first);
      return field ? `${field}: ${msg}` : msg;
    }
    return String(first || "Validation failed");
  }
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function apiError(e, fallback = "Request failed") {
  return formatApiErrorDetail(e?.response?.data?.detail) || e?.message || fallback;
}
